import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
  doc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

import { auth, db } from "../firebase/firebase";
import { errorTextFieldStyles } from "../styles/globalStyles";

import LandingBackground from "../components/Landing/LandingBackground";
import LandingFooter from "../components/Landing/LandingFooter";
import LoginHeader from "../components/Login/LoginHeader";

import { ENGLISH_SKILLS, MATH_SKILLS } from "../data/skillTaxonomy";
import { initSectionAnalytics } from "../utils/analytics/initSectionAnalytics";

const DIAGNOSTICS = [
  { id: "english_diagnostic_v1", section: "English", administeredCount: 36 },
  { id: "math_diagnostic_v1", section: "Math", administeredCount: 36 },
];

const buildBlankDiagnosticAttempt = ({ id, section, administeredCount }) => ({
  diagnosticId: id,
  section,

  status: "not_started",
  currentIndex: 0,

  administeredCount,
  sequence: [],
  responses: {},

  createdAt: serverTimestamp(),
  startedAt: null,
  completedAt: null,
  lastSavedAt: null,

  version: 1,
});

const SignupPage = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!formData.firstName.trim()) e.firstName = "First name is required.";
    if (!formData.lastName.trim()) e.lastName = "Last name is required.";
    if (!formData.email.trim()) e.email = "Email is required.";
    if (!formData.password) e.password = "Password is required.";
    if (formData.password.length < 6)
      e.password = "Password must be at least 6 characters.";
    if (formData.password !== formData.confirmPassword)
      e.confirmPassword = "Passwords do not match.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      /* 1️⃣ Create auth user */
      const cred = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const uid = cred.user.uid;
      const batch = writeBatch(db);

      /* 2️⃣ Create user doc */
      const userRef = doc(db, "users", uid);
      batch.set(userRef, {
        uid,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: "student",
        createdAt: serverTimestamp(),

        analytics: {
          version: 1,
          english: initSectionAnalytics("english", ENGLISH_SKILLS),
          math: initSectionAnalytics("math", MATH_SKILLS),
        },
      });

      /* 3️⃣ Create diagnostic attempts */
      for (const d of DIAGNOSTICS) {
        const attemptRef = doc(
          db,
          "users",
          uid,
          "diagnosticAttempts",
          d.id
        );

        batch.set(attemptRef, buildBlankDiagnosticAttempt(d));
      }

      /* 4️⃣ Commit atomically */
      await batch.commit();

      navigate("/");
    } catch (err) {
      console.error("Signup failed:", err);

      if (err.code === "auth/email-already-in-use") {
        setErrors({ email: "This email is already registered." });
      } else if (err.code === "auth/invalid-email") {
        setErrors({ email: "Please enter a valid email address." });
      } else {
        setErrors({ password: "Signup failed. Please try again." });
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <LandingBackground />

      <Box sx={{ position: "relative", zIndex: 1 }}>
        <LoginHeader />

        <Container maxWidth="sm">
          <Paper
            elevation={4}
            sx={{
              p: 4,
              mt: 10,
              mb: 10,
              borderRadius: 3,
              position: "relative",
              zIndex: 2,
            }}
          >
            <Typography
              variant="h4"
              fontWeight={600}
              mb={3}
              textAlign="center"
            >
              Create Your Account
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              <TextField
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                sx={errors.firstName ? errorTextFieldStyles : {}}
                required
              />

              <TextField
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                sx={errors.lastName ? errorTextFieldStyles : {}}
                required
              />

              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                sx={errors.email ? errorTextFieldStyles : {}}
                required
              />

              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                sx={errors.password ? errorTextFieldStyles : {}}
                required
              />

              <TextField
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                sx={errors.confirmPassword ? errorTextFieldStyles : {}}
                required
              />

              <Button
                type="submit"
                variant="contained"
                sx={{
                  mt: 1,
                  borderRadius: "25px",
                  py: 1.25,
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                Sign Up
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>

      <Box sx={{ mt: "auto", zIndex: 1 }}>
        <LandingFooter />
      </Box>
    </Box>
  );
};

export default SignupPage;
